"use client";

import React, { useState, useEffect } from "react";
import { type SpinningWheelUIReward } from "@cede/types";
import { METRICS_EVENTS } from "@cede/types";
import { WheelRewardCard } from "./WheelRewardCard";
import { WheelRewardNavigation } from "./WheelRewardNavigation";
import { Button } from "@/components/ui/button";
import { PortalModal } from "@/components/ui/portal-modal";
import { ANIMATION_DURATIONS } from "@/constants/spinningWheel";
import { useDevice, useHubUser } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { cn } from "@/lib/utils";
import { isBigPrize, generateTweetText } from "@/utils/wheel";

interface WheelRewardsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  rewards: SpinningWheelUIReward[];
}

/**
 * Dialog for displaying spin rewards with navigation and sharing
 */
export const WheelRewardsDialog: React.FC<WheelRewardsDialogProps> = ({ isOpen, onClose, rewards }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sharedRewards, setSharedRewards] = useState<Set<string>>(new Set());
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(null);
  const { isMobile } = useDevice();
  const { hubUser } = useHubUser();
  const { trackEvent } = useMetricsEvents();

  const currentReward = rewards[currentIndex];
  const hasMoreRewards = currentIndex < rewards.length - 1;
  const hasPreviousRewards = currentIndex > 0;
  const isShared = currentReward ? sharedRewards.has(currentReward.id) : false;
  const isMultiSpin = rewards.length > 1;

  // Check if current reward is a big prize
  const isCurrentBigPrize = currentReward ? isBigPrize(currentReward.rewardAmount, currentReward.rewardSymbol) : false;

  const handleShare = () => {
    if (!currentReward) return;

    // Track when user shares a spinning wheel reward on social media
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_REWARD_SHARED, {
      rewardType: currentReward.rewardSymbol,
      rewardLabel: currentReward.rewardAmount.toNumber(),
      isBigPrize: isBigPrize(currentReward.rewardAmount, currentReward.rewardSymbol),
      currentIndex,
      totalRewards: rewards.length,
      hubUserId: hubUser?.id,
    });

    const tweetText = generateTweetText(currentReward);
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(tweetUrl, "_blank");
    setSharedRewards((prev) => new Set(prev).add(currentReward.id));
  };

  const handleNext = () => {
    if (hasMoreRewards) {
      // Track when user navigates to next reward in multi-spin results
      trackEvent(METRICS_EVENTS.SPINNING_WHEEL_REWARD_NAVIGATION, {
        direction: "next",
        fromIndex: currentIndex,
        toIndex: currentIndex + 1,
        totalRewards: rewards.length,
        hubUserId: hubUser?.id,
      });
      setSlideDirection("left");
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        setSlideDirection(null);
      }, ANIMATION_DURATIONS.SLIDE);
    } else {
      handleDialogClose();
    }
  };

  const handlePrevious = () => {
    if (hasPreviousRewards) {
      // Track when user navigates to previous reward in multi-spin results
      trackEvent(METRICS_EVENTS.SPINNING_WHEEL_REWARD_NAVIGATION, {
        direction: "previous",
        fromIndex: currentIndex,
        toIndex: currentIndex - 1,
        totalRewards: rewards.length,
        hubUserId: hubUser?.id,
      });
      setSlideDirection("right");
      setTimeout(() => {
        setCurrentIndex((prev) => prev - 1);
        setSlideDirection(null);
      }, ANIMATION_DURATIONS.SLIDE);
    }
  };

  const handleDialogClose = () => {
    // Track when user closes the spinning wheel rewards dialog
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_REWARDS_DIALOG_CLOSED, {
      currentIndex,
      totalRewards: rewards.length,
      sharedCount: sharedRewards.size,
      hubUserId: hubUser?.id,
    });
    setCurrentIndex(0);
    setSharedRewards(new Set());
    onClose();
  };

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleDialogClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  if (!isOpen || !currentReward) return null;

  return (
    <PortalModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleDialogClose();
      }}
      className={cn(
        "flex flex-col overflow-hidden max-h-[90vh]",
        "scrollbar-thin scrollbar-thumb-cede-primary/30 scrollbar-track-transparent",
        isMobile ? "" : "w-full max-w-[500px] mx-4",
        "z-modal-high",
      )}
      showCloseButton
      closeButtonClassName={isMobile ? "top-4 right-4" : "top-6 right-6"}
    >
      <PortalModal.Header className="px-6 pt-2">
        <PortalModal.Title className="text-center text-2xl font-bold text-cede-primary px-0">
          Win History
        </PortalModal.Title>
      </PortalModal.Header>
      <PortalModal.Content className="relative flex-1 flex flex-col gap-3 pb-6" scrollable>
        <WheelRewardCard
          reward={currentReward}
          slideDirection={slideDirection}
          prizeNumber={isMultiSpin ? currentIndex + 1 : undefined}
          totalPrizes={isMultiSpin ? rewards.length : undefined}
        />

        {/* Share Button */}
        <Button onClick={handleShare} variant="primary" size="large" className="w-full max-w-sm mx-auto">
          <div className="flex items-center justify-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-white">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            {isCurrentBigPrize ? "Share on X to Claim" : "Share on X"}
          </div>
        </Button>

        {/* Navigation/Action Buttons */}
        {isMultiSpin ? (
          <div className="flex gap-4 w-full max-w-sm mx-auto">
            {hasPreviousRewards && (
              <Button
                onClick={handlePrevious}
                variant="transparent-bordered"
                size="medium"
                className="flex-1"
                disabled={isCurrentBigPrize && !isShared}
              >
                Back
              </Button>
            )}
            {hasMoreRewards ? (
              <Button
                onClick={handleNext}
                variant="transparent-bordered"
                size="medium"
                className="flex-1"
                disabled={isCurrentBigPrize && !isShared}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleDialogClose}
                variant="transparent-bordered"
                size="medium"
                className="flex-1"
                disabled={isCurrentBigPrize && !isShared}
              >
                Finish
              </Button>
            )}
          </div>
        ) : (
          (!isCurrentBigPrize || isShared) && (
            <Button
              onClick={handleDialogClose}
              variant="transparent-bordered"
              size="medium"
              className="w-full max-w-sm mx-auto"
            >
              Close
            </Button>
          )
        )}

        {/* Navigation Dots */}
        <WheelRewardNavigation total={rewards.length} currentIndex={currentIndex} />

        {/* Counter */}
        {rewards.length > 1 && (
          <div className="text-center text-sm text-cede-text-secondary">
            Reward {currentIndex + 1} of {rewards.length}
          </div>
        )}
      </PortalModal.Content>
    </PortalModal>
  );
};
